import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowRight, Sparkles, ShieldCheck, Check, Clock, Monitor, Zap, 
  CheckCircle2, Rocket, FileText, CreditCard, MessageCircle, Lock, Star 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { serviceGuide } from '../../data/serviceGuide';
import { siteLanguage } from '../../lib/seo';

export interface HomeServiceGuideProps {
  loadingPlan?: string | null;
  onCheckout?: (plan: 'Starter' | 'Business' | 'Premium' | 'Boost') => void;
  onOpenConsultation?: (packName: string) => void;
}

export function HomeServiceGuide({ loadingPlan, onCheckout, onOpenConsultation }: HomeServiceGuideProps) {
  const { i18n } = useTranslation();
  const currentLang = siteLanguage(i18n.language?.split('-')[0]);
  const content = serviceGuide[currentLang] || serviceGuide.fr;

  const [activePackId, setActivePackId] = useState<'Essentiel' | 'Croissance' | 'Sur-Mesure'>('Croissance');
  const [activeStepId, setActiveStepId] = useState<number>(1);

  const trackerSteps = [
    {
      id: 1,
      title: currentLang === 'fr' ? 'Brief & Cadrage' : currentLang === 'ar' ? 'التوجيه وتحديد النطاق' : 'Brief & Scoping',
      status: currentLang === 'fr' ? 'En cours' : currentLang === 'ar' ? 'جارٍ العمل' : 'In Progress',
      desc: currentLang === 'fr'
        ? "Sélection de vos objectifs, de votre charte graphique et validation du périmètre technique sous 24h ouvrées."
        : currentLang === 'ar'
        ? "تحديد أهدافك وهويتك البصرية والاتفاق على النطاق التقني خلال 24 ساعة عمل."
        : "Definition of your goals, visual identity, and technical scope validated within 24 business hours.",
      deliverable: currentLang === 'fr' ? 'Cahier des charges & retroplanning validés' : 'Validated specifications & timeline',
      icon: Clock,
    },
    {
      id: 2,
      title: currentLang === 'fr' ? 'Design & Maquettes' : currentLang === 'ar' ? 'التصميم والنماذج' : 'Design & Mockups',
      status: currentLang === 'fr' ? 'Étape 2' : currentLang === 'ar' ? 'المرحلة 2' : 'Step 2',
      desc: currentLang === 'fr'
        ? "Direction artistique sur-mesure sur Figma : prototypage interactif smartphone & grand écran soumis à vos retours."
        : currentLang === 'ar'
        ? "تصميم واجهات وتجربة مستخدم حصرية على Figma مع محاكاة تفاعلية للجوال وسطح المكتب."
        : "Bespoke Figma UI/UX art direction with interactive mobile and desktop prototypes.",
      deliverable: currentLang === 'fr' ? 'Maquettes interactives prêtes pour intégration' : 'Interactive mockups ready for development',
      icon: Monitor,
    },
    {
      id: 3,
      title: currentLang === 'fr' ? 'Développement' : currentLang === 'ar' ? 'التطوير البرمجي' : 'Development',
      status: currentLang === 'fr' ? 'Étape 3' : currentLang === 'ar' ? 'المرحلة 3' : 'Step 3',
      desc: currentLang === 'fr'
        ? "Intégration pure React 19 sans CMS lourd. Vitesse d'affichage sous la seconde et balisage sémantique SEO natif."
        : currentLang === 'ar'
        ? "تطوير شفرة برمجية نقية عبر React دون منصات ثقيلة، بسرعة تحميل فائقة وتهيئة SEO مدمجة."
        : "Pure React engineering with zero bloated CMS. Sub-second load times and native SEO markup.",
      deliverable: currentLang === 'fr' ? 'Code source optimisé & Score PageSpeed 95+' : 'Optimized source code & PageSpeed score 95+',
      icon: Zap,
    },
    {
      id: 4,
      title: currentLang === 'fr' ? 'Revue & Tests' : currentLang === 'ar' ? 'المراجعة والاختبار' : 'Review & QA',
      status: currentLang === 'fr' ? 'Étape 4' : currentLang === 'ar' ? 'المرحلة 4' : 'Step 4',
      desc: currentLang === 'fr'
        ? "Contrôle qualité rigoureux : tests d'affichage multi-appareils, formulaires, passerelle Stripe et conformité RGPD."
        : currentLang === 'ar'
        ? "فحص دقيق وتدقيق شامل لجميع الأجهزة وتجربة النماذج والدفع الإلكتروني والتوافق الأمني."
        : "Rigorous quality assurance across all devices, form endpoints, Stripe integration, and GDPR security.",
      deliverable: currentLang === 'fr' ? 'Validation finale sur environnement de pré-production' : 'Final client sign-off on staging environment',
      icon: CheckCircle2,
    },
    {
      id: 5,
      title: currentLang === 'fr' ? 'Mise en ligne' : currentLang === 'ar' ? 'الإطلاق الرسمي' : 'Launch & Live',
      status: currentLang === 'fr' ? 'Étape 5' : currentLang === 'ar' ? 'المرحلة 5' : 'Step 5',
      desc: currentLang === 'fr'
        ? "Déploiement serveur sécurisé TLS 1.3, connexion du nom de domaine et soumission automatique à Google Search Console."
        : currentLang === 'ar'
        ? "إطلاق على خوادم سحابية آمنة وربط النطاق وبدء الفهرسة الفورية على محرك بحث Google."
        : "Cloud TLS 1.3 deployment, custom domain connection, and automated Google indexing submission.",
      deliverable: currentLang === 'fr' ? 'Site 100% opérationnel & indexé sur Google' : '100% operational website indexed on Google',
      icon: Rocket,
    },
  ];

  const packages = [
    {
      id: 'Essentiel' as const,
      name: currentLang === 'fr' ? 'Pack Essentiel' : currentLang === 'ar' ? 'الباقة الأساسية' : 'Essential Pack',
      planType: 'Starter' as const,
      badge: currentLang === 'fr' ? 'Starter' : currentLang === 'ar' ? 'للبدء السريع' : 'Starter',
      price: '499 €',
      tagline: currentLang === 'fr'
        ? 'Site Vitrine One-Page ou 3 sections • Idéal démarrage rapide'
        : currentLang === 'ar'
        ? 'موقع تعريفي احترافي • مثالي للانطلاق السريع وبناء الثقة'
        : 'One-page or 3-section showcase website • Ideal fast launch',
      delay: currentLang === 'fr' ? 'Livraison 48h chrono' : currentLang === 'ar' ? 'تسليم خلال 48 ساعة' : '48h delivery',
      features: currentLang === 'fr' ? [
        'Site vitrine moderne responsive mobile-first',
        'Formulaire de contact & devis sécurisé',
        'Nom de domaine & hébergement inclus 1 an',
        'Score de performance 95+ garanti',
      ] : currentLang === 'ar' ? [
        'موقع عصري متجاوب بالكامل مع الهواتف الذكية',
        'نموذج طلب عروض أسعار وتواصل آمن',
        'اسم نطاق واستضافة سريعة متضمنة لمدة عام',
        'أداء فائق معتمد 95+ على Google',
      ] : [
        'Modern responsive mobile-first showcase site',
        'Secure quote & contact dynamic form',
        'Custom domain & hosting included for 1 year',
        'Guaranteed Google 95+ performance score',
      ],
    },
    {
      id: 'Croissance' as const,
      name: currentLang === 'fr' ? 'Pack Croissance' : currentLang === 'ar' ? 'باقة النمو' : 'Growth Pack',
      planType: 'Business' as const,
      badge: currentLang === 'fr' ? 'Recommandé • Le plus populaire' : currentLang === 'ar' ? 'موصى به • الأكثر طلباً' : 'Recommended • Best Seller',
      popular: true,
      price: '1 490 €',
      tagline: currentLang === 'fr'
        ? 'Site Multi-pages & SEO Google / Acquisition & conversion'
        : currentLang === 'ar'
        ? 'موقع متكامل متعدد الصفحات وتصدر محركات البحث'
        : 'Multi-page website & Google SEO / Acquisition & conversion',
      delay: currentLang === 'fr' ? 'Livraison 5 à 7 jours' : currentLang === 'ar' ? 'تسليم خلال 5 إلى 7 أيام' : '5 to 7 days delivery',
      features: currentLang === 'fr' ? [
        'Multi-pages complètes (5 à 10 pages optimisées)',
        'Optimisation SEO Google 360° & pack local',
        'Bouton WhatsApp direct & Chat intégré',
        'Section avis certifiés & Google Maps',
        'Formation complète & autonomie totale',
      ] : currentLang === 'ar' ? [
        'موقع متكامل من 5 إلى 10 صفحات مهيأة للتحويل',
        'تهيئة SEO شاملة 360° وتصدر البحث المحلي',
        'زر واتساب مباشر ومحادثة فورية متكاملة',
        'عرض تقييمات موثقة وخريطة Google تفاعلية',
        'تدريب ودليل تشغيل شامل لاستقلالية تامة',
      ] : [
        'Comprehensive multi-page site (5 to 10 pages)',
        '360° Google SEO & local search optimization',
        'Direct WhatsApp button & live chat integration',
        'Certified review section & Google Maps embed',
        'Full handover training for complete independence',
      ],
    },
    {
      id: 'Sur-Mesure' as const,
      name: currentLang === 'fr' ? 'Pack Sur-Mesure' : currentLang === 'ar' ? 'باقة مخصصة' : 'Custom Pack',
      planType: 'Premium' as const,
      badge: currentLang === 'fr' ? 'Entreprise & Métier' : currentLang === 'ar' ? 'للشركات والمتاجر' : 'Enterprise & Custom',
      price: currentLang === 'fr' ? 'Sur devis' : currentLang === 'ar' ? 'حسب المواصفات' : 'Custom Quote',
      tagline: currentLang === 'fr'
        ? 'Application web sur-mesure ou plateforme E-commerce'
        : currentLang === 'ar'
        ? 'تطبيقات ويب متقدمة أو منصات تجارة إلكترونية كاملة'
        : 'Custom web application or full e-commerce platform',
      delay: currentLang === 'fr' ? 'Planning & cadrage dédié' : currentLang === 'ar' ? 'جدول زمني مخصص' : 'Dedicated timeline',
      features: currentLang === 'fr' ? [
        'Architecture React Fullstack sur-mesure',
        'Boutique en ligne ou Espace membre dédié',
        'Connexion API tierces, webhooks & CRM',
        'Support prioritaire 7j/7 & chef de projet',
      ] : currentLang === 'ar' ? [
        'بنية برمجية متكاملة React Fullstack مخصصة',
        'متجر إلكتروني متكامل أو بوابة أعضاء خاصة',
        'ربط برمجي مع الواجهات والأنظمة وCRM',
        'دعم فني مخصص 7/7 ومدير مشروع شخصي',
      ] : [
        'Tailored React full-stack architecture',
        'Online store or dedicated member portal',
        'Third-party API, webhooks & CRM integration',
        'Priority 7/7 support & dedicated project manager',
      ],
    },
  ];

  const activePackage = packages.find((p) => p.id === activePackId) || packages[1];
  const activeStep = trackerSteps.find((s) => s.id === activeStepId) || trackerSteps[0];

  const handleActionCheckout = () => {
    if (activePackage.id === 'Sur-Mesure') {
      if (onOpenConsultation) {
        onOpenConsultation('Sur-Mesure');
      }
    } else if (onCheckout) {
      onCheckout(activePackage.planType);
    }
  };

  const handleActionBrief = () => {
    if (onOpenConsultation) {
      onOpenConsultation(activePackage.name);
    }
  };


  return (
    <section className="py-16 sm:py-24 relative overflow-hidden" id="pricing">
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        
        {/* En-tête de section principal */}
        <div className="max-w-3xl mx-auto text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-muted/80 text-foreground text-xs font-semibold uppercase tracking-wider mb-3 shadow-2xs">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span>
              {currentLang === 'fr' ? 'Méthode Studio & Tarifs Transparents' : currentLang === 'ar' ? 'منهجية الاستوديو والأسعار الشفافة' : 'Studio Methodology & Pricing'}
            </span>
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold tracking-tight font-heading text-foreground">
            {currentLang === 'fr'
              ? 'Votre Projet Web de A à Z : Suivi en Direct & Tarifs Clairs'
              : currentLang === 'ar'
              ? 'مشروعك من البداية حتى الإطلاق: وضوح ومنهجية وأسعار شفافة'
              : 'Your Web Project from A to Z: Live Milestone Tracking & Transparent Pricing'}
          </h2>
          <p className="mt-3 text-xs sm:text-base md:text-lg text-muted-foreground leading-relaxed">
            {currentLang === 'fr'
              ? "Chaque phase est planifiée, mesurée et débloquée en toute clarté. Sélectionnez votre formule ci-dessous pour initialiser votre cockpit et lancer votre brief."
              : currentLang === 'ar'
              ? "كل مرحلة محددة ومنظمة بشفافية تامة. اختر باقتك لتفعيل مساحة مشروعك وإرسال تفاصيل فكرتك."
              : "Every phase is carefully planned, measured, and unlocked transparently. Select your package below to initialize your project cockpit."}
          </p>
        </div>

        {/* Cockpit Interactif de Projet */}
        <div className="rounded-3xl border border-border/80 bg-card/90 backdrop-blur-xl shadow-xl overflow-hidden p-4 sm:p-7 md:p-8 relative">
          
          {/* Cockpit Top Bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-5 border-b border-border/60">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shadow-xs">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold font-heading text-foreground">
                  {currentLang === 'fr' ? 'Studio Flash Web — Cockpit de Conception en Temps Réel' : 'Flash Web Studio — Live Conception Cockpit'}
                </h3>
                <p className="text-[11px] sm:text-xs text-muted-foreground">
                  {currentLang === 'fr' ? 'Simulation de votre espace client • Visibilité étape par étape' : 'Live client workspace simulation • Step-by-step progress'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="border-border bg-muted/80 text-foreground font-semibold text-xs flex items-center gap-1.5 shadow-2xs">
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0 animate-pulse" />
                <span>{currentLang === 'fr' ? 'Formule active :' : 'Active Pack:'} {activePackage.name}</span>
              </Badge>
              <Badge variant="outline" className="border-border bg-muted/80 text-foreground font-semibold text-xs flex items-center gap-1.5 shadow-2xs">
                <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                <span>{currentLang === 'fr' ? '100% Propriétaire' : '100% Owned'}</span>
              </Badge>
            </div>
          </div>

          {/* 5-Step Visual Stepper Bar */}
          <div className="py-6 sm:py-8 border-b border-border/60">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {trackerSteps.map((step) => {
                const Icon = step.icon;
                const isSelected = activeStepId === step.id;
                const isFirst = step.id === 1;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => setActiveStepId(step.id)}
                    className={`p-3 rounded-2xl border text-left transition-all duration-200 cursor-pointer flex flex-col justify-between ${
                      isSelected
                        ? 'border-primary bg-primary/10 shadow-xs ring-1 ring-primary/30'
                        : 'border-border/70 bg-background/60 hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1 mb-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isFirst ? 'bg-primary text-primary-foreground shadow-2xs' : 'bg-muted text-foreground'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        isFirst ? 'bg-emerald-600 text-white shadow-2xs' : 'text-muted-foreground'
                      }`}>
                        {step.status}
                      </span>
                    </div>
                    <div>
                      <span className="text-[11px] font-mono text-muted-foreground block">
                        0{step.id}
                      </span>
                      <h4 className="text-xs sm:text-sm font-semibold text-foreground truncate">
                        {step.title}
                      </h4>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Selected Step Info Pill */}
            <div className="mt-4 p-3.5 rounded-xl border border-border/80 bg-muted/40 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
              <div className="flex items-center gap-2.5 text-xs text-foreground">
                <div className="w-6 h-6 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0 text-xs font-bold shadow-2xs">
                  {activeStep.id}
                </div>
                <div>
                  <strong className="font-bold text-foreground me-1.5">{activeStep.title} :</strong>
                  <span className="text-muted-foreground">{activeStep.desc}</span>
                </div>
              </div>
              <div className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 shrink-0 flex items-center gap-1">
                <Check className="w-3.5 h-3.5 shrink-0" />
                <span>{activeStep.deliverable}</span>
              </div>
            </div>
          </div>

          {/* Formula Selector (3 Cards) */}
          <div className="pt-6 sm:pt-8">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                  {currentLang === 'fr' ? 'Étape 1 sur 5 • Choix de la formule & Cadrage' : 'Step 1 of 5 • Formula & Scope Selection'}
                </span>
                <h4 className="text-lg sm:text-xl font-bold font-heading text-foreground mt-0.5">
                  {currentLang === 'fr' ? 'Sélectionnez votre formule pour configurer votre projet' : 'Select your formula to configure your project'}
                </h4>
              </div>
              <span className="text-xs text-muted-foreground">
                {currentLang === 'fr' ? 'Étapes 2 à 5 débloquées automatiquement dès validation' : 'Steps 2 to 5 unlocked upon confirmation'}
              </span>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {packages.map((pkg) => {
                const isSelected = activePackId === pkg.id;

                return (
                  <div
                    key={pkg.id}
                    onClick={() => setActivePackId(pkg.id)}
                    className={`p-5 sm:p-6 rounded-2xl border transition-all duration-300 flex flex-col justify-between cursor-pointer relative ${
                      isSelected
                        ? 'border-primary bg-card shadow-lg ring-2 ring-primary/40'
                        : 'border-border/80 bg-background/70 hover:border-border hover:bg-card/60'
                    }`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-3 start-1/2 -translate-x-1/2">
                        <Badge className="bg-primary text-primary-foreground font-bold text-[10px] px-2.5 py-0.5 shadow-xs">
                          {pkg.badge}
                        </Badge>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                          {pkg.badge}
                        </span>
                        {isSelected && (
                          <Badge className="bg-emerald-600 text-white font-bold text-[10px] shadow-2xs">
                            ✓ {currentLang === 'fr' ? 'Sélectionné' : 'Selected'}
                          </Badge>
                        )}
                      </div>

                      <h4 className="text-xl font-bold font-heading text-foreground">
                        {pkg.name}
                      </h4>

                      <div className="my-3">
                        <div className="text-2xl sm:text-3xl font-bold text-foreground font-mono">
                          {pkg.price}
                          {pkg.id !== 'Sur-Mesure' && (
                            <span className="text-xs font-normal text-muted-foreground ms-1.5 font-sans">
                              {currentLang === 'fr' ? 'TTC (Net)' : 'All-inclusive'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 leading-snug">
                          {pkg.tagline}
                        </p>
                      </div>

                      <div className="space-y-2 pt-3 border-t border-border/60 mb-5">
                        {pkg.features.map((feat, fIdx) => (
                          <div key={fIdx} className="flex items-start gap-2 text-xs text-foreground font-medium">
                            <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-3 border-t border-border/50 flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>{pkg.delay}</span>
                      <span className={`font-semibold ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                        {isSelected
                          ? (currentLang === 'fr' ? '● Formule active' : '● Active Pack')
                          : (currentLang === 'fr' ? 'Choisir cette offre →' : 'Select pack →')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons Toolbar & Trust Strip */}
            <div className="mt-8 pt-6 border-t border-border/60">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  size="lg"
                  onClick={handleActionBrief}
                  className="w-full sm:w-auto rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-6 h-12 gap-2 shadow-md cursor-pointer text-sm"
                >
                  <FileText className="w-4 h-4" />
                  <span>
                    {currentLang === 'fr'
                      ? `Compléter mon Brief technique (${activePackage.name})`
                      : `Submit technical brief (${activePackage.name})`}
                  </span>
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleActionCheckout}
                  disabled={loadingPlan !== null}
                  className="w-full sm:w-auto rounded-xl border-border bg-muted/80 hover:bg-muted text-foreground font-bold px-6 h-12 gap-2 shadow-xs cursor-pointer text-sm"
                >
                  <CreditCard className="w-4 h-4 text-primary" />
                  <span>
                    {activePackage.id === 'Sur-Mesure'
                      ? (currentLang === 'fr' ? 'Demander mon devis sur-mesure' : 'Request custom quote')
                      : (currentLang === 'fr' ? `Régler la commande • ${activePackage.price}` : `Order now • ${activePackage.price}`)}
                  </span>
                </Button>

                <Button
                  size="lg"
                  variant="ghost"
                  onClick={() => window.dispatchEvent(new CustomEvent('open-live-chat', {
                    detail: {
                      message: `Bonjour Flash Web, je souhaite échanger au sujet du ${activePackage.name}.`,
                      isClient: false
                    }
                  }))}
                  className="w-full sm:w-auto rounded-xl text-muted-foreground hover:text-foreground font-semibold px-4 h-12 gap-2 cursor-pointer text-xs sm:text-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>{currentLang === 'fr' ? "Contacter l'équipe" : 'Live Chat'}</span>
                </Button>
              </div>

              {/* Guarantees Reassurance Row */}
              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground text-center">
                <div className="flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  <span>{currentLang === 'fr' ? 'Paiement sécurisé 3D Secure' : '3D Secure Payment'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5 text-primary" />
                  <span>CB, Apple Pay, Google Pay</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                  <span>{currentLang === 'fr' ? 'Facture avec TVA instantanée' : 'Instant Tax Invoice'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Rocket className="w-3.5 h-3.5 text-amber-500" />
                  <span>{currentLang === 'fr' ? 'Étapes 2 à 5 débloquées à réception' : 'Steps 2-5 unlocked upon receipt'}</span>
                </div>
              </div>
            </div>

          </div>

        </div>

        {/* Liens utiles & maillage local Rennes & Bretagne */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 pt-8 border-t border-border/40 text-xs sm:text-sm">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            <Link to="/audit" className="font-semibold text-foreground hover:text-primary transition-colors flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <span>{currentLang === 'fr' ? 'Scanner son site gratuitement' : currentLang === 'ar' ? 'فحص موقعك مجاناً' : 'Free Website Scanner'}</span>
            </Link>
            <Link to="/blog" className="font-semibold text-foreground hover:text-primary transition-colors">
              {content.resources}
            </Link>
            <span className="hidden md:inline text-muted-foreground/30">•</span>
            <div className="hidden sm:flex items-center gap-2.5 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{currentLang === 'fr' ? 'Rennes & Bretagne :' : 'Rennes & Brittany :'}</span>
              <Link to="/creation-site-internet-rennes" className="hover:text-primary transition-colors">
                {currentLang === 'fr' ? 'Création de site' : 'Web design'}
              </Link>
              <span>·</span>
              <Link to="/creation-site-e-commerce-rennes" className="hover:text-primary transition-colors">
                {currentLang === 'fr' ? 'E-commerce' : 'E-commerce'}
              </Link>
              <span>·</span>
              <Link to="/refonte-site-internet-rennes" className="hover:text-primary transition-colors">
                {currentLang === 'fr' ? 'Refonte' : 'Redesign'}
              </Link>
            </div>
          </div>
          <Link to="/contact" className="font-bold text-primary hover:underline">
            {content.cta} →
          </Link>
        </div>

      </div>
    </section>
  );
}
